// The Quote tab of an RFQ's Supplier Space
// apps/smart-catalog/frontend/src/components/buyer-agent/rfq-collab/QuotePanel.tsx
//
// Supplier (through their link): upload the quotation they already prepare and
// send it. Nothing to fill in. Typing the numbers and "cannot quote" are the
// other two answers. After sending they see a receipt and can send a new version.
//
// Our user: the supplier's files and the numbers we read from them, to check
// against the document. Accepting a quotation stays in the RFQ Center.

import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Stack, TextField, Typography, alpha, useTheme } from '@mui/material';
import {
  CheckCircleOutline as ReceivedIcon,
  CloudUploadOutlined as UploadIcon,
  InsertDriveFileOutlined as FileIcon,
} from '@mui/icons-material';

import apiClient from '../../../services/api';
import { buyerAgentService } from '../../../services/buyerAgentService';
import { rfqPortalService } from '../../../services/rfqPortalService';
import type { Workspace, WorkspaceQuote, WorkspaceQuoteFile } from '../../../services/workspaceService';

const MAX_FILES = 5;
const MAX_FILE_MB = 20;
const ACCEPT = '.pdf,.xlsx,.xls,.csv,.doc,.docx,.png,.jpg,.jpeg,.txt';
const DECLINE_REASONS = ['Outside what we make', 'No capacity now', 'Deadline too short', 'Information missing', 'Other'];
const CURRENCIES = ['USD', 'EUR', 'ILS', 'GBP', 'JPY'];

type View = 'upload' | 'type' | 'decline';

const day = (iso?: string): string =>
  iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
const size = (bytes: number): string => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const money = (value?: number, currency?: string): string =>
  typeof value === 'number' ? `${currency || 'USD'} ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';
const errorText = (err: unknown): string => {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e?.response?.data?.message || e?.message || 'Something went wrong';
};

export interface QuotePanelProps {
  rfqId: string;
  ws: Workspace;
  anchorPartId?: string;
  guest?: boolean;
  token?: string;
}

export default function QuotePanel({ rfqId, ws, anchorPartId, guest = false, token = '' }: QuotePanelProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [view, setView] = useState<View>('upload');
  const [changing, setChanging] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [note, setNote] = useState('');
  const [over, setOver] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [form, setForm] = useState({ unitPrice: '', currency: 'USD', leadTimeDays: '', moq: '', setupNre: '', tooling: '', validityDays: '30', notes: '' });

  const quotes: WorkspaceQuote[] = ws.quotes ?? [];
  const latest = quotes[quotes.length - 1];
  const declined = ws.status === 'REJECTED';
  const anchor = ws.items.find((i) => i.partId === anchorPartId) ?? ws.items.find((i) => !i.informational);
  const refresh = () => qc.invalidateQueries({ queryKey: guest ? ['portal-ws', token] : ['rfq-workspace', rfqId] });
  const num = (v: string): number | undefined => (v.trim() === '' ? undefined : Number(v));

  const add = (picked: FileList | File[] | null) => {
    if (!picked) return;
    const next = [...files];
    for (const f of Array.from(picked)) {
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        setProblem(`${f.name} is larger than ${MAX_FILE_MB} MB`);
        continue;
      }
      if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
    }
    if (next.length > MAX_FILES) setProblem(`Up to ${MAX_FILES} files`);
    else setProblem(null);
    setFiles(next.slice(0, MAX_FILES));
  };

  const done = () => {
    setFiles([]);
    setNote('');
    setChanging(false);
    setView('upload');
    refresh();
  };
  const sendFiles = useMutation({
    mutationFn: () => (guest ? rfqPortalService.submitQuoteFiles(token, files, note) : buyerAgentService.addQuoteFiles(rfqId, files, note)),
    onSuccess: done,
  });
  const sendTyped = useMutation({
    mutationFn: async () => {
      if (guest) {
        return rfqPortalService.submitQuote(token, {
          unitPrice: Number(form.unitPrice) || 0,
          currency: form.currency,
          leadTimeDays: num(form.leadTimeDays),
          validUntilDays: num(form.validityDays),
          setupCost: num(form.setupNre),
          toolingCost: num(form.tooling),
          notes: form.notes.trim() || undefined,
        });
      }
      return buyerAgentService.addQuote(rfqId, {
        sourceType: 'MANUAL_ENTRY',
        quotedCurrency: form.currency,
        validUntil: form.validityDays.trim() ? new Date(Date.now() + Number(form.validityDays) * 86400000).toISOString() : undefined,
        notes: form.notes.trim() || undefined,
        lineItems: [
          {
            lineItemId: anchor?.lineItemId,
            partId: anchor?.partId,
            partNumber: anchor?.partNumber,
            quotedUnitPrice: num(form.unitPrice),
            quotedCurrency: form.currency,
            quotedLeadTimeDays: num(form.leadTimeDays),
            quotedMOQ: num(form.moq),
            setupCost: num(form.setupNre),
            toolingCost: num(form.tooling),
          },
        ],
        totalQuotedPrice: num(form.unitPrice),
      } as Parameters<typeof buyerAgentService.addQuote>[1]);
    },
    onSuccess: done,
  });
  const decline = useMutation({ mutationFn: () => rfqPortalService.setState(token, 'REJECTED', reason), onSuccess: done });

  const openFile = async (file: WorkspaceQuoteFile) => {
    try {
      const blob = guest
        ? await rfqPortalService.quoteFile(token, file.documentId)
        : (await apiClient.get(`/partners/${ws.partnerId}/documents/${file.documentId}/download`, { responseType: 'blob' })).data;
      window.open(URL.createObjectURL(new Blob([blob], { type: file.mimeType || 'application/octet-stream' })), '_blank', 'noopener');
    } catch (err) {
      setProblem(errorText(err));
    }
  };

  const fileRow = (key: string, name: string, detail: string, action: React.ReactNode) => (
    <Stack key={key} direction="row" spacing={1.5} sx={{ alignItems: 'center', border: `1px solid ${theme.palette.divider}`, borderRadius: 2, px: 1.5, py: 1 }}>
      <FileIcon sx={{ color: 'primary.main', flexShrink: 0 }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', overflowWrap: 'anywhere' }}>{name}</Typography>
        <Typography variant="caption" color="text.secondary">{detail}</Typography>
      </Box>
      {action}
    </Stack>
  );
  const sentFiles = (quote: WorkspaceQuote) =>
    quote.documents.map((d) => fileRow(d.documentId, d.fileName, size(d.sizeBytes), <Button size="small" onClick={() => openFile(d)} sx={{ textTransform: 'none' }}>View</Button>));

  const numbers = (quote: WorkspaceQuote) => {
    const line = quote.lineItems.find((li) => typeof li.quotedUnitPrice === 'number');
    if (!line) return null;
    const cells: Array<[string, string]> = [
      ['Unit price', money(line.quotedUnitPrice, line.quotedCurrency || quote.quotedCurrency)],
      ['Lead time', line.quotedLeadTimeDays != null ? `${line.quotedLeadTimeDays} days` : ''],
      ['Min order', line.quotedMOQ != null ? String(line.quotedMOQ) : ''],
      ['Tooling', money(line.toolingCost, quote.quotedCurrency)],
      ['Setup', money(line.setupCost, quote.quotedCurrency)],
      ['Payment', quote.paymentTerms || ''],
      ['Delivery', quote.deliveryTerms || ''],
      ['Valid until', quote.validUntil ? new Date(quote.validUntil).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''],
    ];
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
        {cells.filter(([, v]) => v).map(([k, v]) => (
          <Box key={k} sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">{k}</Typography>
            <Typography sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>{v}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const typedForm = (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
        <TextField size="small" label="Unit price" required type="number" value={form.unitPrice} onChange={(e) => setForm({ ...form, unitPrice: e.target.value })} />
        <TextField size="small" label="Currency" select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
          {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Lead time, days" type="number" value={form.leadTimeDays} onChange={(e) => setForm({ ...form, leadTimeDays: e.target.value })} />
        <TextField size="small" label="Valid for, days" type="number" value={form.validityDays} onChange={(e) => setForm({ ...form, validityDays: e.target.value })} />
        <TextField size="small" label="Tooling" type="number" value={form.tooling} onChange={(e) => setForm({ ...form, tooling: e.target.value })} />
        <TextField size="small" label="Setup" type="number" value={form.setupNre} onChange={(e) => setForm({ ...form, setupNre: e.target.value })} />
        {!guest && <TextField size="small" label="Min order" type="number" value={form.moq} onChange={(e) => setForm({ ...form, moq: e.target.value })} />}
        <TextField size="small" label="Anything to add" multiline minRows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} sx={{ gridColumn: '1 / -1' }} />
      </Box>
      {sendTyped.isError && <Alert severity="error">{errorText(sendTyped.error)}</Alert>}
      <Stack direction="row" spacing={1}>
        <Button variant="contained" disableElevation disabled={!(Number(form.unitPrice) > 0) || sendTyped.isPending} onClick={() => sendTyped.mutate()} sx={{ textTransform: 'none' }}>
          {sendTyped.isPending ? 'Sending' : guest ? 'Send quotation' : 'Add quotation'}
        </Button>
        <Button onClick={() => setView('upload')} sx={{ textTransform: 'none' }}>Back</Button>
      </Stack>
    </Stack>
  );

  const heading = (title: string, sub?: string) => (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{title}</Typography>
      {sub && <Typography variant="body2" color="text.secondary">{sub}</Typography>}
    </Box>
  );

  /** The one upload block, used by the supplier and by our own user loading a quotation they received. */
  const uploader = (copy: { title: string; sub: string; drop: string; send: string; foot?: string }) => (
    <>
      {heading(copy.title, copy.sub)}
      <Box
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}
        sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center', py: files.length ? 3 : 5, px: 3,
          border: `2px dashed ${theme.palette.primary.main}`, borderRadius: 3,
          bgcolor: alpha(theme.palette.primary.main, over ? 0.12 : 0.05),
        }}
      >
        <UploadIcon sx={{ fontSize: 34, color: 'primary.main' }} />
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{copy.drop}</Typography>
        <Typography variant="body2" color="text.secondary">PDF, Excel, Word or a photo. Up to {MAX_FILE_MB} MB.</Typography>
        <Button variant={files.length ? 'outlined' : 'contained'} disableElevation onClick={() => input.current?.click()} sx={{ textTransform: 'none', mt: 0.5 }}>
          {files.length ? 'Add another file' : 'Choose a file'}
        </Button>
        <input ref={input} hidden type="file" multiple accept={ACCEPT} onChange={(e) => { add(e.target.files); e.target.value = ''; }} />
      </Box>
      {files.length > 0 && (
        <>
          <Stack spacing={1}>
            {files.map((f) => fileRow(`${f.name}-${f.size}`, f.name, size(f.size), <Button size="small" onClick={() => setFiles(files.filter((x) => x !== f))} sx={{ textTransform: 'none' }}>Remove</Button>))}
          </Stack>
          <TextField size="small" label="Anything to add (optional)" multiline minRows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </>
      )}
      {(problem || sendFiles.isError) && <Alert severity="error">{problem || errorText(sendFiles.error)}</Alert>}
      {files.length > 0 ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Button variant="contained" disableElevation disabled={sendFiles.isPending} onClick={() => sendFiles.mutate()} startIcon={sendFiles.isPending ? <CircularProgress size={16} color="inherit" /> : undefined} sx={{ textTransform: 'none' }}>
            {sendFiles.isPending ? 'Sending' : copy.send}
          </Button>
          {changing && <Button onClick={() => { setChanging(false); setFiles([]); }} sx={{ textTransform: 'none' }}>Cancel</Button>}
        </Stack>
      ) : (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Button onClick={() => setView('type')} sx={{ textTransform: 'none' }}>Type the numbers</Button>
          {guest && <Button onClick={() => setView('decline')} sx={{ textTransform: 'none', color: 'text.secondary' }}>We cannot quote this</Button>}
          {changing && <Button onClick={() => setChanging(false)} sx={{ textTransform: 'none', color: 'text.secondary' }}>Cancel</Button>}
        </Stack>
      )}
      {copy.foot && <Typography variant="caption" color="text.secondary">{copy.foot}</Typography>}
    </>
  );

  let body: React.ReactNode;
  if (!guest) {
    // ── Our user: sees what arrived, and loads a quotation that reached them another way ──
    const supplier = ws.partnerName ?? 'the supplier';
    const loadCopy = {
      drop: `Drop ${supplier}'s quotation here`,
      send: 'Add quotation',
      foot: 'It is kept with this RFQ, shown in the conversation, and its numbers are read for you to check.',
    };
    if (view === 'type') {
      body = <>{heading('Type the quotation', `${ws.rfqNumber} · ${anchor?.partNumber ?? ''}`)}{typedForm}</>;
    } else if (latest && !changing) {
      body = (
        <>
          {heading(`${supplier}'s quotation`, `Version ${latest.revision ?? quotes.length} of ${quotes.length} · received ${day(latest.receivedAt)}`)}
          {latest.documents.length > 0 && <Stack spacing={1}>{sentFiles(latest)}</Stack>}
          {numbers(latest) ? (
            <>
              {latest.documents.length > 0 && latest.extractedBy === 'AI' && (
                <Typography variant="caption" color="text.secondary">Read from the file. Check it against the document.</Typography>
              )}
              {numbers(latest)}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">No numbers on this quotation yet. They are read from the file within a minute when it has readable text.</Typography>
          )}
          {latest.notes && <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{latest.notes}</Typography>}
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button variant="contained" disableElevation onClick={() => navigate(`/rfq/${rfqId}`)} sx={{ textTransform: 'none' }}>Review in RFQ Center</Button>
            <Button variant="outlined" onClick={() => setChanging(true)} sx={{ textTransform: 'none' }}>Add a new version</Button>
          </Stack>
        </>
      );
    } else if (latest) {
      body = uploader({ ...loadCopy, title: 'Add a new version', sub: `Replaces version ${quotes.length} as the latest. Earlier versions are kept.` });
    } else {
      body = uploader({
        ...loadCopy,
        title: declined ? `${supplier} cannot quote this` : `Waiting for ${supplier}'s quotation`,
        sub: declined
          ? ws.supplierDeclineReason || 'No reason given.'
          : `It arrives here when they upload it through their link or reply to the email.${ws.replyBy ? ` Reply by ${new Date(ws.replyBy).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}.` : ''} Got it another way? Add it here.`,
      });
    }
  } else if (view === 'type') {
    body = <>{heading('Type your quotation', `${anchor?.partNumber ?? ''}${anchor?.requestedQuantity ? ` · ${anchor.requestedQuantity} ${anchor.unit ?? 'each'}` : ''}`)}{typedForm}</>;
  } else if (view === 'decline') {
    body = (
      <>
        {heading('Cannot quote this request', 'Telling us why takes one click.')}
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
          {DECLINE_REASONS.map((r) => (
            <Chip key={r} label={r} clickable color={reason === r ? 'primary' : 'default'} variant={reason === r ? 'filled' : 'outlined'} onClick={() => setReason(r)} />
          ))}
        </Stack>
        {decline.isError && <Alert severity="error">{errorText(decline.error)}</Alert>}
        <Stack direction="row" spacing={1}>
          <Button variant="contained" disableElevation disabled={!reason || decline.isPending} onClick={() => decline.mutate()} sx={{ textTransform: 'none' }}>Send</Button>
          <Button onClick={() => setView('upload')} sx={{ textTransform: 'none' }}>Back</Button>
        </Stack>
      </>
    );
  } else if (latest && !changing) {
    body = (
      <>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <ReceivedIcon sx={{ fontSize: 40, color: 'success.main' }} />
          {heading('We have your quotation', `Received ${day(latest.receivedAt)}${quotes.length > 1 ? ` · version ${quotes.length}` : ''}`)}
        </Stack>
        {latest.documents.length > 0 ? <Stack spacing={1}>{sentFiles(latest)}</Stack> : numbers(latest)}
        <Typography variant="body2" color="text.secondary">The buyer reviews it and comes back to you by email.</Typography>
        <Box><Button variant="outlined" onClick={() => setChanging(true)} sx={{ textTransform: 'none' }}>Send a new version</Button></Box>
      </>
    );
  } else if (declined && !changing) {
    body = (
      <>
        {heading('You told us you cannot quote this', ws.supplierDeclineReason)}
        <Box><Button variant="outlined" onClick={() => setChanging(true)} sx={{ textTransform: 'none' }}>Send a quotation after all</Button></Box>
      </>
    );
  } else {
    body = uploader({ title: 'Send your quotation', sub: 'Upload the quotation you already prepare. That is all we need.', drop: 'Drop your quotation here', send: 'Send quotation', foot: 'You can also reply to our email with the quotation attached.' });
  }

  return (
    <Box sx={{ flex: 1, overflow: 'auto', p: 3, bgcolor: '#f7f9fc' }}>
      <Paper variant="outlined" sx={{ maxWidth: 720, mx: 'auto', borderRadius: 2, p: 3 }}>
        <Stack spacing={2.25}>{body}</Stack>
      </Paper>
    </Box>
  );
}
